// /admin/rules — Rules list (mockups/access-preview-v6.html "rules" view).
//
// Plain table-of-rules surface: every active rule in the workspace, filtered
// client-side by search/status/role/site. Clicking a row opens the rule in
// its group context (that's where rules are actually managed) — this page
// doesn't have a detail or edit form of its own; the per-row Edit action
// jumps to /admin/rules/{id}/edit.

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
  type MouseEvent,
} from "react";
import { useNavigate } from "react-router-dom";
import { ApiError, useApiErrorHandler } from "@/api";
import { useToast } from "@/components";
import { ruleClient } from "@/data/clients/ruleClient";
import { roleClient } from "@/data/clients/roleClient";
import { locationsClient } from "@/api/locationsClient";
import { useRole } from "@/role";
import type { Rule } from "@/types/rule";
import type { Role } from "@/types/role";
import type { Location } from "@/types/location";
import { relativeTime, roleClass } from "@/pages/admin/people/format";
import "../AdminShared.css";

// The list endpoint returns a Rule, but the BFF sometimes attaches a
// `users_count` field on the list rows for the "Affects" column. We treat
// it as optional and fall back to "—" rather than fan-out N+1 reads.
interface RuleListItem extends Rule {
  users_count?: number;
}

type StatusFilter = "" | "enabled" | "disabled";

export function RulesListPage(): JSX.Element {
  const navigate = useNavigate();
  const handleApiError = useApiErrorHandler();
  const toast = useToast();
  const { currentRole } = useRole();

  const [rules, setRules] = useState<RuleListItem[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");
  const [roleFilter, setRoleFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesResp, rolesResp, locationsResp] = await Promise.all([
        ruleClient.list({ limit: 200 }),
        roleClient.list({ limit: 200 }),
        locationsClient.list().catch(() => [] as Location[]),
      ]);
      setRules(rulesResp.items);
      setRoles(rolesResp.items);
      setLocations(locationsResp);
    } catch (err) {
      if (err instanceof ApiError) {
        handleApiError(err, { actionLabel: "Load rules" });
      }
    } finally {
      setLoading(false);
    }
  }, [handleApiError]);

  useEffect(() => {
    if (currentRole === null) return;
    void load();
  }, [currentRole, load]);

  // -------------------------------------------------------------------------
  // Lookups
  // -------------------------------------------------------------------------

  const roleMap = useMemo<Map<string, Role>>(() => {
    const map = new Map<string, Role>();
    for (const r of roles) map.set(r.role_id, r);
    return map;
  }, [roles]);

  const locationNameMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const l of locations) map.set(l.ring_location_id, l.display_name);
    return map;
  }, [locations]);

  // Distinct role IDs that appear on at least one rule — drives the role
  // dropdown so the operator only sees options that would actually filter.
  const distinctRoleIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rules) set.add(r.role_id);
    return Array.from(set).sort();
  }, [rules]);

  // -------------------------------------------------------------------------
  // Filter
  // -------------------------------------------------------------------------

  const filteredRules = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rules.filter((rule) => {
      if (statusFilter === "enabled" && !rule.enabled) return false;
      if (statusFilter === "disabled" && rule.enabled) return false;
      if (roleFilter !== "" && rule.role_id !== roleFilter) return false;
      if (siteFilter !== "") {
        if (rule.location_scope.mode === "ALL") {
          // ALL covers every site — keep it visible regardless of site filter.
        } else if (!rule.location_scope.ids.includes(siteFilter)) {
          return false;
        }
      }
      if (q.length === 0) return true;
      const role = roleMap.get(rule.role_id);
      const haystack = [
        rule.display_name,
        rule.rule_id,
        rule.group_match?.group_name ?? "",
        rule.role_id,
        role?.display_name ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [rules, search, statusFilter, roleFilter, siteFilter, roleMap]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const onToggleEnabled = useCallback(
    async (rule: RuleListItem) => {
      const next = !rule.enabled;
      try {
        const resp = await ruleClient.update(
          rule.rule_id,
          { enabled: next },
          rule.version,
        );
        setRules((prev) =>
          prev.map((r) =>
            r.rule_id === rule.rule_id
              ? { ...r, ...resp.rule, users_count: r.users_count }
              : r,
          ),
        );
        toast.success({
          title: next ? "Rule enabled" : "Rule disabled",
          description: rule.display_name,
        });
      } catch (err) {
        if (err instanceof ApiError) {
          handleApiError(err, {
            actionLabel: next ? "Enable rule" : "Disable rule",
          });
        }
      }
    },
    [handleApiError, toast],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <section className="admin-page">
      <header className="v6-page-header">
        <div className="row">
          <h1>Rules</h1>
          <div className="actions">
            <button
              type="button"
              className="v6-btn primary"
              onClick={() => navigate("/admin/rules/new")}
            >
              + Create rule
            </button>
          </div>
        </div>
        <div className="caption">
          Every rule that&apos;s active in this workspace. A rule binds a group
          to a role at a scope. Click a rule to open its detail view —
          that&apos;s where rules are managed.
        </div>
      </header>

      <div className="v6-filter-row">
        <div className="search-wrap">
          <span className="ico" aria-hidden="true">
            ⌕
          </span>
          <input
            type="search"
            placeholder="Search rules by name, group, or role…"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            aria-label="Search rules"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          aria-label="Filter rules by status"
        >
          <option value="">Status: any</option>
          <option value="enabled">Enabled</option>
          <option value="disabled">Disabled</option>
        </select>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label="Filter rules by role"
        >
          <option value="">Role: any</option>
          {distinctRoleIds.map((id) => (
            <option key={id} value={id}>
              {id.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={siteFilter}
          onChange={(e) => setSiteFilter(e.target.value)}
          aria-label="Filter rules by site"
        >
          <option value="">Site: any</option>
          {locations.map((l) => (
            <option key={l.ring_location_id} value={l.ring_location_id}>
              {l.display_name}
            </option>
          ))}
        </select>
        <span className="count">
          {loading
            ? "Loading…"
            : `${filteredRules.length} rule${filteredRules.length === 1 ? "" : "s"}`}
        </span>
      </div>

      <table className="v6-table rules-table">
        <thead>
          <tr>
            <th>Rule</th>
            <th>Group</th>
            <th>Role</th>
            <th>Scope</th>
            <th>Affects</th>
            <th>Status</th>
            <th>Last modified</th>
            <th style={{ textAlign: "right" }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows columns={8} />
          ) : filteredRules.length === 0 ? (
            <tr>
              <td colSpan={8} className="admin-muted" style={{ padding: 24, textAlign: "center" }}>
                {rules.length === 0
                  ? "No rules yet"
                  : "No rules match your filters."}
              </td>
            </tr>
          ) : (
            filteredRules.map((rule) => (
              <RuleRow
                key={rule.rule_id}
                rule={rule}
                role={roleMap.get(rule.role_id) ?? null}
                totalLocations={locations.length}
                locationName={(id) => locationNameMap.get(id)}
                onOpen={() => navigate(`/admin/rules/${rule.rule_id}`)}
                onEdit={() => navigate(`/admin/rules/${rule.rule_id}/edit`)}
                onToggleEnabled={() => void onToggleEnabled(rule)}
              />
            ))
          )}
        </tbody>
      </table>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface RuleRowProps {
  rule: RuleListItem;
  role: Role | null;
  totalLocations: number;
  locationName: (id: string) => string | undefined;
  onOpen: () => void;
  onEdit: () => void;
  onToggleEnabled: () => void;
}

/** Build the "scope" cell text. */
function formatScope(
  rule: Rule,
  totalLocations: number,
  locationName: (id: string) => string | undefined,
): string {
  const ls = rule.location_scope;
  const ds = rule.device_scope;

  let where: string;
  if (ls.mode === "ALL") {
    where =
      totalLocations > 0
        ? `all ${totalLocations} site${totalLocations === 1 ? "" : "s"}`
        : "all sites";
  } else if (ls.ids.length === 1) {
    const id = ls.ids[0];
    where = id ? (locationName(id) ?? id) : "—";
  } else if (ls.ids.length === 0) {
    where = ls.mode === "DENY" ? "no sites" : "no sites";
  } else {
    where = `${ls.ids.length} site${ls.ids.length === 1 ? "" : "s"}`;
  }

  let cameras: string;
  if (ds.mode === "ALL") {
    cameras = "all cameras";
  } else if (ds.ids.length === 1) {
    cameras = "1 camera";
  } else {
    cameras = `${ds.ids.length} camera${ds.ids.length === 1 ? "" : "s"}`;
  }

  return `${where} · ${cameras}`;
}

function RuleRow({
  rule,
  role,
  totalLocations,
  locationName,
  onOpen,
  onEdit,
  onToggleEnabled,
}: RuleRowProps): JSX.Element {
  const scopeText = formatScope(rule, totalLocations, locationName);
  const roleDisplay = role?.display_name ?? rule.role_id;
  const lastModifiedIso = rule.updated_at ?? rule.created_at;
  const lastModifiedBy = rule.updated_by ?? rule.created_by;

  const stop = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation();
  };

  return (
    <tr className="clickable" onClick={onOpen} style={{ cursor: "pointer" }}>
      <td className="alias">
        <div className="alias-stack">
          <b>{rule.display_name}</b>
          <span className="alias-id">in {rule.group_match?.group_name ?? "—"}</span>
        </div>
      </td>
      <td>{rule.group_match?.group_name ?? "—"}</td>
      <td>
        <span className={`v6-role-chip ${roleClass(rule.role_id)}`}>
          {roleDisplay}
        </span>
      </td>
      <td style={{ fontSize: 13, color: "var(--ring-neutral-300)" }}>
        {scopeText}
      </td>
      <td>
        {typeof rule.users_count === "number"
          ? `${rule.users_count} user${rule.users_count === 1 ? "" : "s"}`
          : "—"}
      </td>
      <td>
        <span
          className={`v6-status-pip ${rule.enabled ? "enabled" : "disabled"}`}
        />
        {rule.enabled ? "Enabled" : "Disabled"}
      </td>
      <td style={{ color: "var(--ring-neutral-300)", fontSize: 13 }}>
        {relativeTime(lastModifiedIso)}
        <br />
        <span style={{ fontSize: 11 }}>by {lastModifiedBy}</span>
      </td>
      <td className="row-actions">
        <button
          type="button"
          className="v6-btn ghost xs"
          onClick={(e) => {
            stop(e);
            onEdit();
          }}
        >
          Edit
        </button>
        <button
          type="button"
          className="v6-btn ghost xs"
          onClick={(e) => {
            stop(e);
            onToggleEnabled();
          }}
        >
          {rule.enabled ? "Disable" : "Enable"}
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Skeleton rows
// ---------------------------------------------------------------------------

function SkeletonRows({ columns }: { columns: number }): JSX.Element {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <tr key={i} aria-hidden="true">
          {Array.from({ length: columns }, (_, c) => (
            <td key={c}>
              <div
                style={{
                  height: 14,
                  background: "var(--ring-tertiary-bg)",
                  borderRadius: 4,
                  width: c === 0 ? "60%" : c === columns - 1 ? "30%" : "40%",
                }}
              />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}
